import type {
  DraftNodeEnvelope,
  DraftNodeWire,
  GetProcessDraftResponse
} from '@bliks-io/contracts';
import {
  stripStableIdsFromInputs,
  type DraftNode
} from '../mutations/helpers/draftGraphOperations';
import {
  DraftNodeNotFoundError,
  type DraftGraphNode,
  type DraftNodeKind,
  type DraftNodeRef
} from './types';

/**
 * Pure draft-graph core: one interface over the three cache arrays of
 * `GetProcessDraftResponse` (`steps[]` / `draftAssignments[]` / `events[]`),
 * keyed by `stableStepId` and routed by node kind. Every routing switch is
 * exhaustive (`satisfies never`) so adding a fourth collection is a compile
 * error here instead of a silent cache miss somewhere else.
 *
 * All functions are reference-preserving: untouched arrays (and the whole
 * response, when nothing changed) keep their identity so react-query
 * subscribers don't re-render for no-op writes.
 */

function eventsOf(draft: GetProcessDraftResponse): NonNullable<GetProcessDraftResponse['events']> {
  return draft.events ?? [];
}

/**
 * Locate a node by id across all three collections. `stableStepId` is the
 * primary id space (React Flow node ids, edge endpoints, input refs); the
 * document `_id` is accepted as a fallback for steps and assignments because
 * persistence routes hand those out. Event rows match by `stableStepId` only —
 * their storage `_id` is draft-internal and never circulates as a node id.
 */
export function findDraftNode(
  draft: GetProcessDraftResponse,
  nodeId: string
): DraftGraphNode | undefined {
  const step = draft.steps.find((s) => String(s.stableStepId) === nodeId);
  if (step) return { kind: 'step', node: step };
  const assignment = draft.draftAssignments.find((a) => String(a.stableStepId) === nodeId);
  if (assignment) return { kind: 'assignment', node: assignment };
  const event = eventsOf(draft).find((e) => String(e.stableStepId) === nodeId);
  if (event) return { kind: 'event', node: event };

  const stepById = draft.steps.find((s) => String(s._id) === nodeId);
  if (stepById) return { kind: 'step', node: stepById };
  const assignmentById = draft.draftAssignments.find((a) => String(a._id) === nodeId);
  if (assignmentById) return { kind: 'assignment', node: assignmentById };
  return undefined;
}

function kindMatches(
  kind: DraftNodeKind,
  expectedKind: DraftNodeKind | readonly DraftNodeKind[]
): boolean {
  return Array.isArray(expectedKind) ? expectedKind.includes(kind) : expectedKind === kind;
}

/**
 * Resolve a node id to its kind + both identity spaces, failing fast on a
 * miss or kind mismatch with {@link DraftNodeNotFoundError}. Replaces the
 * `stableToDraftIdOrSelf` idiom (optional assignments param + silent
 * self-fallback): callers either get a verified ref or a typed error they can
 * treat as a no-op while the settle backstop refetches truth.
 */
export function resolveNodeRef(
  draft: GetProcessDraftResponse,
  nodeId: string,
  expectedKind?: DraftNodeKind | readonly DraftNodeKind[]
): DraftNodeRef {
  const found = findDraftNode(draft, nodeId);
  if (!found) throw new DraftNodeNotFoundError(nodeId, expectedKind);
  if (expectedKind && !kindMatches(found.kind, expectedKind)) {
    throw new DraftNodeNotFoundError(nodeId, expectedKind, found.kind);
  }
  switch (found.kind) {
    case 'step':
    case 'assignment':
      return {
        kind: found.kind,
        backendId: String(found.node._id),
        stableId: String(found.node.stableStepId)
      };
    case 'event':
      // Event endpoints route by the public event id, which IS the row's
      // stableStepId (the storage `_id` never leaves the backend).
      return {
        kind: found.kind,
        backendId: String(found.node.stableStepId),
        stableId: String(found.node.stableStepId)
      };
    default:
      return found satisfies never;
  }
}

function evictByStableId<T extends { stableStepId: unknown }>(arr: T[], stableId: string): T[] {
  const next = arr.filter((n) => String(n.stableStepId) !== stableId);
  return next.length === arr.length ? arr : next;
}

function evictByStableIds<T extends { stableStepId: unknown }>(
  arr: T[],
  stableIds: ReadonlySet<string>
): T[] {
  const next = arr.filter((n) => !stableIds.has(String(n.stableStepId)));
  return next.length === arr.length ? arr : next;
}

function replaceOrAppend<T extends { stableStepId: unknown }>(
  arr: T[],
  stableId: string,
  doc: T,
  mergeWithCached: (cached: T, incoming: T) => T = (_cached, incoming) => incoming
): T[] {
  let replaced = false;
  const next = arr.map((n) => {
    if (String(n.stableStepId) !== stableId) return n;
    replaced = true;
    return mergeWithCached(n, doc);
  });
  return replaced ? next : [...arr, doc];
}

function keepManualCapacity<T extends { manualCapacity?: unknown }>(cached: T, incoming: T): T {
  return incoming.manualCapacity === undefined && cached.manualCapacity != null
    ? { ...incoming, manualCapacity: cached.manualCapacity }
    : incoming;
}

/**
 * Write one wire node into the cache array selected by its `nodeKind`:
 * replace the existing doc keyed by `String(stableStepId)`, or append when
 * absent. A cross-collection move (the convert endpoints report the node
 * under its NEW kind) evicts the stable id from the sibling arrays first, so
 * kind-swaps fall out of the routing instead of needing special cases.
 *
 * The assignment arm strips the wire-only `nodeKind` stamp before storing —
 * cached assignment rows mirror the GET response shape, which doesn't carry
 * the field.
 *
 * Step nodes merge `manualCapacity` instead of replacing it: every draft
 * mutation but the two manual-capacity endpoints answers with a node loaded
 * without the field (it is `select: false`), so an absent key means "unknown,
 * keep what you hold" and only an explicit `null` clears the value.
 */
export function upsertDraftNode(
  draft: GetProcessDraftResponse,
  wire: DraftNodeWire
): GetProcessDraftResponse {
  const stableId = String(wire.stableStepId);
  switch (wire.nodeKind) {
    case 'step':
      return {
        ...draft,
        steps: replaceOrAppend(draft.steps, stableId, wire, keepManualCapacity),
        draftAssignments: evictByStableId(draft.draftAssignments, stableId),
        events: draft.events && evictByStableId(draft.events, stableId)
      };
    case 'assignment': {
      const { nodeKind: _stamp, ...assignment } = wire;
      return {
        ...draft,
        steps: evictByStableId(draft.steps, stableId),
        draftAssignments: replaceOrAppend(draft.draftAssignments, stableId, assignment),
        events: draft.events && evictByStableId(draft.events, stableId)
      };
    }
    case 'event':
      return {
        ...draft,
        steps: evictByStableId(draft.steps, stableId),
        draftAssignments: evictByStableId(draft.draftAssignments, stableId),
        events: replaceOrAppend(eventsOf(draft), stableId, wire)
      };
    default:
      return wire satisfies never;
  }
}

/**
 * Drop `event` inputs linked to the removed nodes and strip their stable ids
 * from every `previous_output` source (dropping emptied input rows) — the
 * union of the server-side cleanups for event deletes and step/assignment
 * deletes. One pass covers both because the caller doesn't know the removed
 * nodes' kinds (delete envelopes carry bare `deletedNodeIds`).
 */
function stripDanglingRefs<T extends DraftNode>(node: T, removedStableIds: ReadonlySet<string>): T {
  const inputs = node.inputs ?? [];
  const withoutEventRefs = inputs.filter(
    (input) =>
      input.inputType !== 'event' || !input.eventId || !removedStableIds.has(String(input.eventId))
  );
  const base =
    withoutEventRefs.length === inputs.length ? node : ({ ...node, inputs: withoutEventRefs } as T);
  return stripStableIdsFromInputs(base, removedStableIds);
}

/**
 * Remove a batch of nodes by `stableStepId` from whichever collections hold
 * them and strip the dangling input refs they leave behind in steps and
 * assignments — one evict+strip pass over each collection regardless of
 * batch size. Reference-preserving when none of the ids are present.
 */
export function removeDraftNodes(
  draft: GetProcessDraftResponse,
  stableIds: ReadonlySet<string>
): GetProcessDraftResponse {
  if (stableIds.size === 0) return draft;

  const cleanCollection = <T extends DraftNode>(arr: T[]): T[] => {
    const evicted = evictByStableIds(arr, stableIds);
    let anyStripped = false;
    const stripped = evicted.map((n) => {
      const cleaned = stripDanglingRefs(n, stableIds);
      if (cleaned !== n) anyStripped = true;
      return cleaned;
    });
    if (anyStripped) return stripped;
    return evicted;
  };

  const steps = cleanCollection(draft.steps);
  const draftAssignments = cleanCollection(draft.draftAssignments);
  const events = draft.events && evictByStableIds(draft.events, stableIds);

  if (
    steps === draft.steps &&
    draftAssignments === draft.draftAssignments &&
    events === draft.events
  ) {
    return draft;
  }
  return { ...draft, steps, draftAssignments, events };
}

/**
 * Apply `fn` to the node identified by `nodeId` (same lookup semantics as
 * {@link findDraftNode}), wherever it lives. `fn` receives the located
 * `{ kind, node }` entry and must return a node of the same kind — return the
 * received node unchanged to no-op. Bumps `draft.updatedAt` iff the node
 * actually changed; preserves all references otherwise.
 */
export function transformDraftNode(
  draft: GetProcessDraftResponse,
  nodeId: string,
  fn: (entry: DraftGraphNode) => DraftGraphNode['node']
): GetProcessDraftResponse {
  const entry = findDraftNode(draft, nodeId);
  if (!entry) return draft;
  const next = fn(entry);
  if (next === entry.node) return draft;
  const bumped = { ...draft.draft, updatedAt: new Date() };
  switch (entry.kind) {
    case 'step':
      return {
        ...draft,
        draft: bumped,
        steps: draft.steps.map((s) => (s === entry.node ? (next as typeof s) : s))
      };
    case 'assignment':
      return {
        ...draft,
        draft: bumped,
        draftAssignments: draft.draftAssignments.map((a) =>
          a === entry.node ? (next as typeof a) : a
        )
      };
    case 'event':
      return {
        ...draft,
        draft: bumped,
        events: eventsOf(draft).map((e) => (e === entry.node ? (next as typeof e) : e))
      };
    default:
      return entry satisfies never;
  }
}

/**
 * THE generic cache writer for `DraftNodeEnvelope` responses: deletions go
 * through {@link removeDraftNodes} (one pass for the whole batch), every reported node through
 * {@link upsertDraftNode}, routed purely by the envelope's `nodeKind`
 * discriminator (cache rows don't reliably carry one). Bumps
 * `draft.updatedAt` once iff anything changed; returns `prev` untouched for
 * an empty/no-op envelope.
 */
export function reconcileEnvelope(
  prev: GetProcessDraftResponse,
  envelope: DraftNodeEnvelope
): GetProcessDraftResponse {
  let next = removeDraftNodes(prev, new Set(envelope.deletedNodeIds.map(String)));
  for (const wire of envelope.nodes) {
    next = upsertDraftNode(next, wire);
  }
  if (next === prev) return prev;
  return { ...next, draft: { ...next.draft, updatedAt: new Date() } };
}

/**
 * The envelope's single node of the given kind — for callers that need the
 * created/updated doc itself (e.g. placeholder swaps pairing it with
 * `createdNodeIds`). Throws when the envelope doesn't carry exactly one,
 * which signals a frontend/backend contract drift, not a user error.
 */
export function expectSingleNode<K extends DraftNodeWire['nodeKind']>(
  envelope: DraftNodeEnvelope,
  kind: K
): Extract<DraftNodeWire, { nodeKind: K }> {
  const matches = envelope.nodes.filter(
    (n): n is Extract<DraftNodeWire, { nodeKind: K }> => n.nodeKind === kind
  );
  if (matches.length !== 1) {
    throw new Error(`Expected exactly one '${kind}' node in envelope, got ${matches.length}`);
  }
  return matches[0];
}
